import { COLORES_TIPOS } from '../config/colores-visitas.config';

/**
 * Mapeo centralizado id/descripción de tipo de visita → clase CSS.
 * Retrocompatible con catálogo anterior (ids 1–4, mismas descripciones legacy).
 */

/** Campos numéricos del modelo de evolución mensual por tipo (ids 1–5). */
export type CampoEvolucionTipoVisita =
  | 'diagnostico'
  | 'diseno_plan_negocio'
  | 'asesoria_base'
  | 'asesoria_especialistas_individual'
  | 'encuesta_margen_bruto';

/** Conteos por campo para gráfico de evolución en dashboard. */
export type ConteosEvolucionTipoVisita = Record<CampoEvolucionTipoVisita, number>;

/** Descripción normalizada → campo interno (catálogo actual + alias legacy). */
export const CAMPO_EVOLUCION_POR_DESCRIPCION: Record<string, CampoEvolucionTipoVisita> = {
  diagnostico: 'diagnostico',
  'diseno plan de negocio': 'diseno_plan_negocio',
  diseno: 'diseno_plan_negocio',
  'formulacion del plan': 'diseno_plan_negocio',
  'asesoria base': 'asesoria_base',
  implementacion: 'asesoria_base',
  asesoria: 'asesoria_base',
  'asesoria de especialistas individual': 'asesoria_especialistas_individual',
  'bolsa de visitas': 'asesoria_especialistas_individual',
  'bolsa de horas': 'asesoria_especialistas_individual',
  'asesoria extraordinaria': 'asesoria_especialistas_individual',
  'encuesta margen bruto': 'encuesta_margen_bruto'
};

/** Series del gráfico «Evolución de Tipos de Visita» (orden ids 1–5). */
export const SERIES_EVOLUCION_TIPOS_VISITA: {
  label: string;
  campo: CampoEvolucionTipoVisita;
  color: string;
}[] = [
  { label: 'Diagnóstico', campo: 'diagnostico', color: COLORES_TIPOS.diagnostico.hex },
  {
    label: 'Diseño plan de negocio',
    campo: 'diseno_plan_negocio',
    color: COLORES_TIPOS.disenoPlanNegocio.hex
  },
  { label: 'Asesoría base', campo: 'asesoria_base', color: COLORES_TIPOS.asesoriaBase.hex },
  {
    label: 'Asesoría de especialistas individual',
    campo: 'asesoria_especialistas_individual',
    color: COLORES_TIPOS.asesoriaEspecialistasIndividual.hex
  },
  {
    label: 'Encuesta margen bruto',
    campo: 'encuesta_margen_bruto',
    color: COLORES_TIPOS.encuestaMargenBruto.hex
  }
];

/** Conteos en cero para los cinco tipos del catálogo. */
export function crearConteosEvolucionVacios(): ConteosEvolucionTipoVisita {
  return {
    diagnostico: 0,
    diseno_plan_negocio: 0,
    asesoria_base: 0,
    asesoria_especialistas_individual: 0,
    encuesta_margen_bruto: 0
  };
}

/**
 * Mapea `cantidad_tipos` de la API (claves = descripción del catálogo) al modelo interno.
 */
export function mapearCantidadTiposAEvolucion(
  cantidadTipos: Record<string, number>
): ConteosEvolucionTipoVisita {
  const conteos = crearConteosEvolucionVacios();
  for (const [etiqueta, valor] of Object.entries(cantidadTipos ?? {})) {
    const campo = CAMPO_EVOLUCION_POR_DESCRIPCION[normalizarDescripcionTipo(etiqueta)];
    if (campo) {
      conteos[campo] += Number(valor) || 0;
    }
  }
  return conteos;
}

/** Clases CSS para badges y popovers de tipos de visita */
export const CLASE_TIPO_DEFAULT = 'tipo-default';

export const CLASES_TIPO_VISITA = {
  diagnostico: 'tipo-diagnostico',
  disenoPlanNegocio: 'tipo-diseno-plan-negocio',
  asesoriaBase: 'tipo-asesoria-base',
  asesoriaEspecialistasIndividual: 'tipo-asesoria-especialistas-individual',
  encuestaMargenBruto: 'tipo-encuesta-margen-bruto',
  default: CLASE_TIPO_DEFAULT
} as const;

/** id_tipo_visita → clase CSS (catálogo actual y legacy comparten ids 1–4) */
const CLASE_POR_ID: Record<number, string> = {
  1: CLASES_TIPO_VISITA.diagnostico,
  2: CLASES_TIPO_VISITA.disenoPlanNegocio,
  3: CLASES_TIPO_VISITA.asesoriaBase,
  4: CLASES_TIPO_VISITA.asesoriaEspecialistasIndividual,
  5: CLASES_TIPO_VISITA.encuestaMargenBruto
};

/** Descripción normalizada → clase CSS (nuevas + legacy) */
const CLASE_POR_DESCRIPCION: Record<string, string> = {
  diagnostico: CLASES_TIPO_VISITA.diagnostico,
  'diseno plan de negocio': CLASES_TIPO_VISITA.disenoPlanNegocio,
  diseno: CLASES_TIPO_VISITA.disenoPlanNegocio,
  'formulacion del plan': CLASES_TIPO_VISITA.disenoPlanNegocio,
  'asesoria base': CLASES_TIPO_VISITA.asesoriaBase,
  implementacion: CLASES_TIPO_VISITA.asesoriaBase,
  asesoria: CLASES_TIPO_VISITA.asesoriaBase,
  'asesoria de especialistas individual': CLASES_TIPO_VISITA.asesoriaEspecialistasIndividual,
  'bolsa de visitas': CLASES_TIPO_VISITA.asesoriaEspecialistasIndividual,
  'bolsa de horas': CLASES_TIPO_VISITA.asesoriaEspecialistasIndividual,
  'asesoria extraordinaria': CLASES_TIPO_VISITA.asesoriaEspecialistasIndividual,
  'encuesta margen bruto': CLASES_TIPO_VISITA.encuestaMargenBruto
};

/**
 * Normaliza descripción: minúsculas, sin acentos, espacios colapsados.
 */
export function normalizarDescripcionTipo(descripcion: string): string {
  return (descripcion ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

/** Clase CSS por id_tipo_visita (1–5). */
export function getClaseTipoVisitaPorId(idTipoVisita: number): string {
  return CLASE_POR_ID[idTipoVisita] ?? CLASE_TIPO_DEFAULT;
}

/**
 * Resuelve clase CSS: prioriza id_tipo_visita; si no hay, usa descripción (nueva o legacy).
 */
export function getClaseTipoVisita(
  descripcion?: string | null,
  idTipoVisita?: number | null
): string {
  if (idTipoVisita != null && idTipoVisita > 0) {
    const porId = CLASE_POR_ID[idTipoVisita];
    if (porId) return porId;
  }

  const normalizada = normalizarDescripcionTipo(descripcion ?? '');
  if (!normalizada) return CLASE_TIPO_DEFAULT;

  return CLASE_POR_DESCRIPCION[normalizada] ?? CLASE_TIPO_DEFAULT;
}
